import { Avatar } from './Avatar';
import { SessionBadge } from './SessionBadge';
import { StatusTicks } from './StatusTicks';
import { formatInboxTime } from '../utils/time-format';
import type { ConversationRow } from '../api/whatsapp.api';

export function ConversationItem({
  row,
  selected,
  onClick,
}: {
  row: ConversationRow;
  selected: boolean;
  onClick: () => void;
}) {
  const displayName = row.contactName || `+${row.contactWaId}`;
  const lastTs = row.lastMessage?.timestamp ?? row.lastInboundAt ?? row.lastOutboundAt ?? row.updatedAt;
  const hasUnread = row.unreadCount > 0;

  // Preview: "You: …" for outbound, plain text for inbound.
  const preview = row.lastMessage?.bodyPreview ?? '';
  const previewPrefix =
    row.lastMessage?.direction === 'OUTBOUND' ? <span className="opacity-60">You: </span> : null;

  return (
    <button
      onClick={onClick}
      type="button"
      className={`group relative flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors border-b border-gray-100 ${
        selected ? 'bg-indigo-50' : 'hover:bg-slate-50'
      }`}
      aria-current={selected ? 'true' : undefined}
    >
      {/* Selected-state left bar */}
      {selected && <span className="absolute left-0 top-0 h-full w-1 bg-indigo-500" />}

      <div className="relative">
        <Avatar name={displayName} size={44} />
        <div className="absolute -bottom-0.5 -right-0.5 rounded-full bg-white p-0.5">
          <SessionBadge
            lastInboundAt={row.lastInboundAt}
            sessionExpiresAt={row.sessionExpiresAt}
            compact
          />
        </div>
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span
            className={`truncate text-sm ${hasUnread ? 'font-semibold text-slate-900' : 'text-slate-800'}`}
          >
            {displayName}
          </span>
          <span className="flex-shrink-0 text-[11px] text-slate-400">
            {formatInboxTime(lastTs)}
          </span>
        </div>

        <div className="mt-0.5 flex items-center gap-1.5">
          {row.lastMessage?.direction === 'OUTBOUND' && (
            <span className="flex-shrink-0 text-slate-400">
              <StatusTicks status={row.lastMessage.status} size={12} />
            </span>
          )}
          <span
            className={`truncate text-[12.5px] ${hasUnread ? 'font-medium text-slate-700' : 'text-slate-500'}`}
          >
            {previewPrefix}
            {preview || <span className="italic text-slate-400">no messages yet</span>}
          </span>
        </div>

        <div className="mt-1 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
            {row.assignedAgent ? (
              <>
                <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-slate-200 text-[9px] font-bold text-slate-600">
                  {row.assignedAgent.name.charAt(0).toUpperCase()}
                </span>
                <span className="truncate max-w-[120px]">{row.assignedAgent.name}</span>
              </>
            ) : (
              <span className="italic text-slate-400">Unassigned</span>
            )}
          </div>
          {hasUnread && (
            <span className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-indigo-500 px-1.5 text-[11px] font-bold text-white">
              {row.unreadCount}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}
